import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Header } from '../components/Header';
import { useAuth } from '../contexts/AuthContext';
import { AuctionCard } from '../components/AuctionCard';
import { Button } from '../components/ui/button';
import { Heart, Gavel } from 'lucide-react';
import api from '../utils/api';

export const Watchlist = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [watchlist, setWatchlist] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadWatchlist();
  }, []);

  const loadWatchlist = async () => {
    try {
      const response = await api.get('/user/watchlist');
      setWatchlist(response.data);
    } catch (error) {
      console.error('Failed to load watchlist:', error);
    } finally {
      setLoading(false);
    }
  };

  const removeFromWatchlist = async (auctionId) => {
    try {
      await api.delete(`/user/watchlist/${auctionId}`);
      setWatchlist(watchlist.filter(item => item.auction.auction_id !== auctionId));
    } catch (error) {
      console.error('Failed to remove from watchlist:', error);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />

      <div className="container mx-auto px-4 md:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="mb-12">
          <h1 className="text-4xl md:text-6xl font-bold tracking-tighter font-heading mb-2" data-testid="watchlist-heading">
            My Watchlist
          </h1>
          <p className="text-lg text-muted-foreground">
            Keep track of auctions you're interested in
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
          <div className="border border-border p-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-muted-foreground">Watched Items</span>
              <Heart className="h-5 w-5 text-red-500" strokeWidth={1.5} />
            </div>
            <div className="text-3xl font-bold font-mono">{watchlist.length}</div>
          </div>
          <div className="border border-border p-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-muted-foreground">Active Auctions</span>
              <Gavel className="h-5 w-5 text-primary" strokeWidth={1.5} />
            </div>
            <div className="text-3xl font-bold font-mono text-primary">
              {watchlist.filter(item => item.auction.status === 'active').length}
            </div>
          </div>
        </div>

        {/* Watchlist Items */}
        {loading ? (
          <div className="text-center py-20 text-muted-foreground font-mono">Loading watchlist...</div>
        ) : watchlist.length === 0 ? (
          <div className="text-center py-20">
            <Heart className="h-16 w-16 mx-auto mb-4 text-muted-foreground" strokeWidth={1} />
            <p className="text-muted-foreground text-lg mb-4">Your watchlist is empty</p>
            <p className="text-muted-foreground mb-6">Browse auctions and click the heart icon to add items to your watchlist.</p>
            <Button onClick={() => navigate('/browse')} data-testid="browse-to-watch-button">
              <Gavel className="h-4 w-4 mr-2" strokeWidth={1.5} />
              Browse Auctions
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="watchlist-grid">
            {watchlist.map((item) => (
              <AuctionCard 
                key={item.watchlist_id} 
                auction={item.auction}
                showWatchlistButton={true}
                isInWatchlist={true}
                onWatchlistToggle={() => removeFromWatchlist(item.auction.auction_id)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
